/**
 * Employee detail page fixture data — client/project breakdown, review items,
 * app usage, and activity bars for the EmployeeDetailPage.
 *
 * Mirrors the skill-test EmployeeDetail.tsx data for Sarah Chen.
 */

export interface EmployeeDetailStat {
  label: string;
  value: string;
  sub?: string;
  subMuted?: boolean;
}

export const EMPLOYEE_DETAIL_STATS: readonly EmployeeDetailStat[] = [
  { label: 'Total Hours', value: '142.5h' },
  { label: 'Billable Hours', value: '118.2h', sub: '83% billable' },
  { label: 'Avg Billable / Day', value: '6.2h', sub: 'Team avg 5.2h', subMuted: true },
  { label: 'Effective Days', value: '18.5' },
  { label: 'Full Days', value: '16' },
  { label: 'Half Days', value: '5' },
];

export interface EmployeeProjectRow {
  name: string;
  type: 'billable' | 'general';
  hours: number;
  events: number;
  pct: number;
}

export interface EmployeeClientGroup {
  id: string;
  name: string;
  isLink: boolean;
  hours: number;
  events: number;
  pct: number;
  projects: readonly EmployeeProjectRow[];
}

export const EMPLOYEE_CLIENT_GROUPS: readonly EmployeeClientGroup[] = [
  {
    id: 'meridian',
    name: 'Meridian Capital',
    isLink: true,
    hours: 62.3,
    events: 748,
    pct: 44,
    projects: [
      { name: 'Corporate Restructuring', type: 'billable', hours: 62.3, events: 748, pct: 44 },
    ],
  },
  {
    id: 'techventure',
    name: 'TechVenture Labs',
    isLink: true,
    hours: 35.8,
    events: 430,
    pct: 25,
    projects: [
      { name: 'Patent Portfolio Review', type: 'billable', hours: 35.8, events: 430, pct: 25 },
    ],
  },
  {
    id: 'pinnacle',
    name: 'Pinnacle Financial',
    isLink: true,
    hours: 20.1,
    events: 241,
    pct: 14,
    projects: [
      { name: 'Regulatory Compliance', type: 'billable', hours: 20.1, events: 241, pct: 14 },
    ],
  },
  {
    id: 'general',
    name: 'General / Internal',
    isLink: false,
    hours: 24.3,
    events: 291,
    pct: 17,
    projects: [
      { name: 'Internal Training', type: 'general', hours: 14.2, events: 170, pct: 10 },
      { name: 'Firm Administration', type: 'general', hours: 10.1, events: 121, pct: 7 },
    ],
  },
];

export interface ReviewItem {
  app: string;
  title: string;
  screenshots: number;
  lastSeen: string;
  sessions: number;
  time: string;
  dateRange: string;
}

// Task 188: banner aggregates for the "Needs Your Review" section in mockup
// mode. The banner copy on /employees/:employeeId reads `REVIEW_TOTAL_HOURS`
// and `REVIEW_TOTAL_SESSIONS` (preserves the historical 8.4h / 12 sessions
// visual identity for design / a11y snapshot tests). `REVIEW_ITEMS_TOTAL`
// matches `REVIEW_ITEMS.length` so the "see all 17" link on the detail page
// resolves to a populated full-list page on /employees/:employeeId/review.
// Live mode uses the `totalHours` / `totalSessions` / `totalGroups` values
// returned by the backend instead.
export const REVIEW_TOTAL_HOURS = 8.4;
export const REVIEW_TOTAL_SESSIONS = 12;
export const REVIEW_ITEMS_TOTAL = 17;

export const REVIEW_ITEMS: readonly ReviewItem[] = [
  {
    app: 'Microsoft Word',
    title: 'Document1',
    screenshots: 18,
    lastSeen: 'Apr 11',
    sessions: 18,
    time: '2.4h',
    dateRange: 'Apr 7 \u2013 Apr 11',
  },
  {
    app: 'Chrome',
    title: 'Untitled',
    screenshots: 9,
    lastSeen: 'Apr 10',
    sessions: 9,
    time: '1.8h',
    dateRange: 'Apr 8 \u2013 Apr 10',
  },
  {
    app: 'VS Code',
    title: 'Untitled-1',
    screenshots: 7,
    lastSeen: 'Apr 9',
    sessions: 7,
    time: '1.4h',
    dateRange: 'Apr 9 \u2013 Apr 9',
  },
  {
    app: 'Excel',
    title: 'Book1',
    screenshots: 5,
    lastSeen: 'Apr 8',
    sessions: 5,
    time: '1.1h',
    dateRange: 'Apr 7 \u2013 Apr 8',
  },
  // The 13 rows below extend the fixture so /employees/:employeeId/review
  // renders a populated page in mockup mode. Their hours sum to 1.7h:
  // 6.7h (top-4 above) + 1.7h = 8.4h, matching REVIEW_TOTAL_HOURS.
  {
    app: 'Slack',
    title: 'general',
    screenshots: 6,
    lastSeen: 'Apr 11',
    sessions: 3,
    time: '0.3h',
    dateRange: 'Apr 9 \u2013 Apr 11',
  },
  {
    app: 'Notion',
    title: 'Meeting Notes',
    screenshots: 3,
    lastSeen: 'Apr 10',
    sessions: 2,
    time: '0.15h',
    dateRange: 'Apr 9 \u2013 Apr 10',
  },
  {
    app: 'Zoom',
    title: 'Daily Standup',
    screenshots: 3,
    lastSeen: 'Apr 11',
    sessions: 3,
    time: '0.15h',
    dateRange: 'Apr 8 \u2013 Apr 11',
  },
  {
    app: 'Figma',
    title: 'Untitled',
    screenshots: 3,
    lastSeen: 'Apr 9',
    sessions: 2,
    time: '0.15h',
    dateRange: 'Apr 8 \u2013 Apr 9',
  },
  {
    app: 'Spotify',
    title: 'Focus Playlist',
    screenshots: 3,
    lastSeen: 'Apr 11',
    sessions: 3,
    time: '0.15h',
    dateRange: 'Apr 7 \u2013 Apr 11',
  },
  {
    app: 'Terminal',
    title: 'bash',
    screenshots: 2,
    lastSeen: 'Apr 10',
    sessions: 2,
    time: '0.1h',
    dateRange: 'Apr 9 \u2013 Apr 10',
  },
  {
    app: 'Postman',
    title: 'Workspace',
    screenshots: 2,
    lastSeen: 'Apr 9',
    sessions: 1,
    time: '0.1h',
    dateRange: 'Apr 9 \u2013 Apr 9',
  },
  {
    app: 'Adobe Acrobat',
    title: 'contract.pdf',
    screenshots: 2,
    lastSeen: 'Apr 10',
    sessions: 2,
    time: '0.1h',
    dateRange: 'Apr 8 \u2013 Apr 10',
  },
  {
    app: 'Calculator',
    title: 'Calculator',
    screenshots: 2,
    lastSeen: 'Apr 11',
    sessions: 1,
    time: '0.1h',
    dateRange: 'Apr 11 \u2013 Apr 11',
  },
  {
    app: 'Preview',
    title: 'screenshot.png',
    screenshots: 2,
    lastSeen: 'Apr 9',
    sessions: 2,
    time: '0.1h',
    dateRange: 'Apr 8 \u2013 Apr 9',
  },
  {
    app: 'Finder',
    title: 'Downloads',
    screenshots: 2,
    lastSeen: 'Apr 10',
    sessions: 2,
    time: '0.1h',
    dateRange: 'Apr 9 \u2013 Apr 10',
  },
  {
    app: 'Activity Monitor',
    title: 'CPU',
    screenshots: 2,
    lastSeen: 'Apr 11',
    sessions: 1,
    time: '0.1h',
    dateRange: 'Apr 11 \u2013 Apr 11',
  },
  {
    app: 'System Settings',
    title: 'General',
    screenshots: 2,
    lastSeen: 'Apr 11',
    sessions: 1,
    time: '0.1h',
    dateRange: 'Apr 11 \u2013 Apr 11',
  },
];

export interface AppUsageRow {
  name: string;
  hours: number;
  pct: number;
  muted?: boolean;
}

export const APP_USAGE: readonly AppUsageRow[] = [
  { name: 'Microsoft Word', hours: 42.1, pct: 30 },
  { name: 'Outlook', hours: 28.4, pct: 20 },
  { name: 'Westlaw', hours: 22.6, pct: 16 },
  { name: 'Microsoft Teams', hours: 18.3, pct: 13 },
  { name: 'Chrome', hours: 15.7, pct: 11 },
  { name: 'Adobe Acrobat', hours: 9.6, pct: 7 },
  { name: 'Other', hours: 5.8, pct: 4, muted: true },
];

export const ACTIVITY_BARS: readonly number[] = [
  65, 72, 58, 80, 45, 20, 10, 68, 75, 82, 55, 90, 15, 8, 70, 63, 88, 50, 78, 12, 5, 85, 60, 95, 42,
  100, 18, 7, 73, 66,
];

// ── Task 139 — Jira worklog fixtures ─────────────────────────────────
//
// Three canonical shapes keyed by employee id:
//   - `sarah-chen-id`     — single credential, three projects.
//   - `martin-wu-id`      — two credentials with overlapping project
//                           keys (exercises credential-label
//                           disambiguation).
//   - `employee-no-jira`  — enabled + zero worklogs in range (empty
//                           state).
// The mockup-mode hook looks up by employee id; a missing entry means
// "enabled=false" (no Jira for this org) — that path is exercised by
// passing `mockupOrgWithoutJira: true` on the hook's return object.
//
// All fixtures derive staleness from FROZEN_NOW — the "Last synced N
// minutes ago" pill reads `jira.lastSyncedAt`.

import { FROZEN_NOW_MS } from '@/lib/frozenTime';

export interface JiraIssueRow {
  issueKey: string;
  summary: string;
  statusName: string;
  statusCategory: 'new' | 'indeterminate' | 'done';
  issueArchived: boolean;
  hours: number;
  entries: number;
  browseUrl: string;
}

export interface JiraProjectGroup {
  key: string;
  credentialId: string;
  credentialLabel: string;
  siteUrl: string;
  projectKey: string;
  projectName: string;
  projectArchived: boolean;
  hours: number;
  entries: number;
  issues: readonly JiraIssueRow[];
}

export interface JiraWorklogsFixture {
  enabled: boolean;
  lastSyncedAt: string | null;
  groups: readonly JiraProjectGroup[];
}

const FRESH_SYNC_AT = new Date(FROZEN_NOW_MS - 8 * 60 * 1000).toISOString();
const STALE_SYNC_AT = new Date(FROZEN_NOW_MS - 45 * 60 * 1000).toISOString();

// Single credential, three projects (the common case).
const SINGLE_CREDENTIAL_FIXTURE: JiraWorklogsFixture = {
  enabled: true,
  lastSyncedAt: FRESH_SYNC_AT,
  groups: [
    {
      key: 'cred-fgr:MER',
      credentialId: 'cred-fgr',
      credentialLabel: 'FGR',
      siteUrl: 'https://example-fgr.atlassian.net',
      projectKey: 'MER',
      projectName: 'Meridian Capital',
      projectArchived: false,
      hours: 18.5,
      entries: 22,
      issues: [
        {
          issueKey: 'MER-142',
          summary: 'Q2 restructuring analysis',
          statusName: 'In Progress',
          statusCategory: 'indeterminate',
          issueArchived: false,
          hours: 9.8,
          entries: 12,
          browseUrl: 'https://example-fgr.atlassian.net/browse/MER-142',
        },
        {
          issueKey: 'MER-155',
          summary: 'Stakeholder coordination',
          statusName: 'To Do',
          statusCategory: 'new',
          issueArchived: false,
          hours: 5.2,
          entries: 6,
          browseUrl: 'https://example-fgr.atlassian.net/browse/MER-155',
        },
        {
          issueKey: 'MER-160',
          summary: 'Document review (archived issue)',
          statusName: 'Done',
          statusCategory: 'done',
          issueArchived: false,
          hours: 3.5,
          entries: 4,
          browseUrl: 'https://example-fgr.atlassian.net/browse/MER-160',
        },
      ],
    },
    {
      key: 'cred-fgr:TEC',
      credentialId: 'cred-fgr',
      credentialLabel: 'FGR',
      siteUrl: 'https://example-fgr.atlassian.net',
      projectKey: 'TEC',
      projectName: 'TechVenture Labs',
      projectArchived: false,
      hours: 12.3,
      entries: 18,
      issues: [
        {
          issueKey: 'TEC-41',
          summary: 'Patent portfolio review',
          statusName: 'In Progress',
          statusCategory: 'indeterminate',
          issueArchived: false,
          hours: 8.1,
          entries: 11,
          browseUrl: 'https://example-fgr.atlassian.net/browse/TEC-41',
        },
        {
          issueKey: 'TEC-52',
          summary: 'Filing preparation',
          statusName: 'Done',
          statusCategory: 'done',
          issueArchived: false,
          hours: 4.2,
          entries: 7,
          browseUrl: 'https://example-fgr.atlassian.net/browse/TEC-52',
        },
      ],
    },
    {
      key: 'cred-fgr:PIN',
      credentialId: 'cred-fgr',
      credentialLabel: 'FGR',
      siteUrl: 'https://example-fgr.atlassian.net',
      projectKey: 'PIN',
      projectName: 'Pinnacle Financial (archived)',
      projectArchived: true,
      hours: 4.0,
      entries: 5,
      issues: [
        {
          issueKey: 'PIN-9',
          summary: 'Compliance review',
          statusName: 'Done',
          statusCategory: 'done',
          issueArchived: true,
          hours: 4.0,
          entries: 5,
          browseUrl: 'https://example-fgr.atlassian.net/browse/PIN-9',
        },
      ],
    },
  ],
};

// Two credentials with overlapping project keys — exercises the
// credential-label disambiguation path.
const MULTI_CREDENTIAL_FIXTURE: JiraWorklogsFixture = {
  enabled: true,
  lastSyncedAt: STALE_SYNC_AT,
  groups: [
    {
      key: 'cred-a:OPS',
      credentialId: 'cred-a',
      credentialLabel: 'Clickworx — production',
      siteUrl: 'https://example-cwrx.atlassian.net',
      projectKey: 'OPS',
      projectName: 'Operations',
      projectArchived: false,
      hours: 11.0,
      entries: 14,
      issues: [
        {
          issueKey: 'OPS-3',
          summary: 'Onboarding new client',
          statusName: 'In Progress',
          statusCategory: 'indeterminate',
          issueArchived: false,
          hours: 11.0,
          entries: 14,
          browseUrl: 'https://example-cwrx.atlassian.net/browse/OPS-3',
        },
      ],
    },
    {
      key: 'cred-b:OPS',
      credentialId: 'cred-b',
      credentialLabel: 'Contrix — admin',
      siteUrl: 'https://example-contrix.atlassian.net',
      projectKey: 'OPS',
      projectName: 'Ops',
      projectArchived: false,
      hours: 6.5,
      entries: 9,
      issues: [
        {
          issueKey: 'OPS-17',
          summary: 'Billing reconciliation',
          statusName: 'To Do',
          statusCategory: 'new',
          issueArchived: false,
          hours: 6.5,
          entries: 9,
          browseUrl: 'https://example-contrix.atlassian.net/browse/OPS-17',
        },
      ],
    },
  ],
};

const EMPTY_IN_RANGE_FIXTURE: JiraWorklogsFixture = {
  enabled: true,
  lastSyncedAt: FRESH_SYNC_AT,
  groups: [],
};

/** Fallback used when mockup mode is on for an employee id that
 *  doesn't have a dedicated fixture. */
export const DEFAULT_JIRA_WORKLOG_FIXTURE: JiraWorklogsFixture = SINGLE_CREDENTIAL_FIXTURE;

// Keyed by EMPLOYEES_WITH_STATS id — see src/data/employees.ts. A
// missing entry resolves to `DEFAULT_JIRA_WORKLOG_FIXTURE` at the
// hook's call site.
export const JIRA_WORKLOG_FIXTURE: Record<string, JiraWorklogsFixture> = {
  'sarah-chen-id': SINGLE_CREDENTIAL_FIXTURE,
  'martin-wu-id': MULTI_CREDENTIAL_FIXTURE,
  'employee-no-jira-time': EMPTY_IN_RANGE_FIXTURE,
};
